import angular from 'angular'

import envRaw from '../../utils/env-raw'
import Emitter from '../../utils/emitter'
import attachGetters from '../../utils/attach-getters'

import { defineGetters } from '../../utils/define-getter'
import { once, inRange, uniqueId, constant } from 'lodash-es'
import { makeOR, makeEQ, makeAND, defaultTo, zeroIfNaN } from '../../utils/bits'

import Template from './gallery-image.component.ejs'

angular
  .module('ggs.gallery-image')
  .factory('isImageContent', IsImageContentProvider)
  .component('ggsGalleryImage', {
    template: Template({
      LE: envRaw.liveEditMode
    }),
    controller: GalleryImageComponent,
    bindings: {
      ggsPost: '<',
      ggsMedia: '<',
      ggsProperties: '<',
      ggsContentReadyHandler: '<',
      ggsContentChangeHandler: '<',
      ggsContentAttachCallback: '<'
    }
  })

function IsImageContentProvider() {
  return function isImageContent(media) {
    return media.info.isSomeImage
  }
}

const FAIL = uniqueId('fail')
const DONE = uniqueId('done')
const REDO = uniqueId('redo')

const SIZE_404 = { width: 404, height: 404 }

function GalleryImageComponent(
  qh,
  $scope,
  imgQueue,
  DefineMethod,
  DefineHandler,
  ImageStateMachine) {

  const $ctrl = $scope.$ctrl = attachGetters(this)
  const sign = `galleryImage[id=${$ctrl.ggsPost.id}][mi=${$ctrl.ggsMedia.media_index}][uid=${uniqueId()}]`

  $ctrl.DONE = DONE
  $ctrl.FAIL = FAIL
  $ctrl.REDO = REDO

  $ctrl.wrapM = DefineMethod(sign)
  $ctrl.wrapH = DefineHandler(sign)

  $ctrl.initsThumb = false
  $ctrl.showsThumb = false
  $ctrl.initsImage = false
  $ctrl.showsImage = false
  $ctrl.showsRetry = false

  $ctrl.thumbStatus = null
  $ctrl.imageStatus = null

  const recalcState = ImageStateMachine($ctrl)

  $ctrl.viewThumbSizeEmitter = Emitter(`${sign}:viewThumbSize`)
  $ctrl.viewThumbSize = undefined

  $ctrl.viewImageSizeEmitter = Emitter(`${sign}:viewImageSize`)
  $ctrl.viewImageSize = undefined

  $ctrl.thumbSize = imgQueue.lookup($ctrl.ggsMedia.file_thumb)
  $ctrl.imageSize = imgQueue.lookup($ctrl.ggsMedia.file)

  defineGetters($ctrl, {
    ready: makeOR(
      $ctrl.getters.thumbSize.terminate,
      $ctrl.getters.imageSize.terminate,
      makeAND(
        makeEQ($ctrl.getters.thumbStatus, constant(FAIL)),
        makeOR(
          makeEQ($ctrl.getters.imageStatus, constant(FAIL)),
          makeEQ($ctrl.getters.imageStatus, constant(REDO)))))
  })

  // wrappers

  $ctrl.wrapM('callContentReadyHandler',
    qh.makeIf(
      $ctrl.getters.ready,
      once($ctrl.ggsContentReadyHandler)))

  $ctrl.wrapM('callContentChangeHandler',
    $ctrl.ggsContentChangeHandler)

  // $onInit

  $ctrl.wrapM('attachToParent', () => {
    return $ctrl.ggsContentAttachCallback(
      $ctrl.extContentCalcWidth,
      $ctrl.extContentCalcHeight,
      $ctrl.extContentApplySize)
  })

  $ctrl.wrapH('$onInit',
    qh.makeJoin(
      $ctrl.attachToParent,
      $ctrl.callContentReadyHandler))

  // <ggs-gallery-modal/>

  $ctrl.extContentCalcWidth = (showcaseHeight) => {
    const sourceSize = defaultTo($ctrl.thumbSize, $ctrl.imageSize, SIZE_404)
    return zeroIfNaN((sourceSize.width / sourceSize.height) * showcaseHeight)
  }

  $ctrl.extContentCalcHeight = (showcaseWidth) => {
    const sourceSize = defaultTo($ctrl.thumbSize, $ctrl.imageSize, SIZE_404)
    return zeroIfNaN((sourceSize.height / sourceSize.width) * showcaseWidth)
  }

  $ctrl.wrapM('extContentApplySize', (showcaseSize) => {
    $ctrl.viewThumbSize = calcContentSize(showcaseSize, defaultTo($ctrl.thumbSize, SIZE_404))
    $ctrl.viewImageSize = calcContentSize(showcaseSize, defaultTo($ctrl.imageSize, $ctrl.thumbSize, SIZE_404))
    $ctrl.viewThumbSizeEmitter.emit()
    $ctrl.viewImageSizeEmitter.emit()
  })

  // <gallery-retry/>

  $ctrl.wrapH('retryRequestHandler', () => {
    $ctrl.imageStatus = REDO
    imgQueue.forget($ctrl.ggsMedia.file)
    recalcState()
  })

  // <img-queue/>

  $ctrl.wrapH('thumbLoadDoneHandler', (thumbSize) => {
    $ctrl.thumbSize = thumbSize
    $ctrl.thumbStatus = DONE
    recalcState()
    return qh.join(
      $ctrl.callContentReadyHandler(),
      $ctrl.callContentChangeHandler())
  })

  $ctrl.wrapH('thumbLoadFailHandler', () => {
    $ctrl.thumbStatus = FAIL
    recalcState()
    return $ctrl.callContentReadyHandler()
  })

  $ctrl.wrapH('imageLoadDoneHandler', (imageSize) => {
    $ctrl.imageSize = imageSize
    $ctrl.imageStatus = DONE
    recalcState()
    return qh.join(
      $ctrl.callContentReadyHandler(),
      $ctrl.callContentChangeHandler())
  })

  $ctrl.wrapH('imageLoadFailHandler', () => {
    $ctrl.imageStatus = FAIL
    recalcState()
    return $ctrl.callContentReadyHandler()
  })

  // helpers

  function calcContentSize(showcaseSize, sourceSize) {
    const calcWidth = zeroIfNaN((sourceSize.width / sourceSize.height) * showcaseSize.height)
    const calcHeight = zeroIfNaN((sourceSize.height / sourceSize.width) * showcaseSize.width)

    const fillHor = inRange(showcaseSize.width - calcWidth, 0, 30)
    const fillVer = inRange(showcaseSize.height - calcHeight, 0, 30)
    const useWidth = (fillHor || fillVer) ? fillHor : (calcHeight < showcaseSize.height)

    const width = useWidth ? showcaseSize.width : 'auto'
    const height = !useWidth ? showcaseSize.height : 'auto'
    const marginTop = useWidth ? Math.round((showcaseSize.height - calcHeight) / 2) : 0
    const marginLeft = !useWidth ? Math.round((showcaseSize.width - calcWidth) / 2) : 0

    return { width, height, marginTop, marginLeft }
  }
}
